'use client';

import clsx from 'clsx';
import dayjs from 'dayjs';
import { useState } from 'react';

import { useUpdateTaskMutation } from '@/features/tasks/api/tasksApi';
import { DatePicker, Popover } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './UpdateTaskDueDate.module.scss';

import type { Dayjs } from 'dayjs';

type UpdateTaskDueDateProps = {
  taskId: string;
  value: string | null;
  onUpdated?: () => void;
};

function toIsoStartOfDay(value: Dayjs | null) {
  if (!value || !value.isValid()) {
    return null;
  }

  return value.startOf('day').toISOString();
}

export function UpdateTaskDueDate({
  taskId,
  value,
  onUpdated,
}: UpdateTaskDueDateProps) {
  const [updateTask] = useUpdateTaskMutation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isOpen = Boolean(anchorEl);
  const dueDate = value ? dayjs(value) : null;
  const label =
    dueDate && dueDate.isValid() ? dueDate.format('MMM D') : 'Set date';

  const handleDateChange = async (nextValue: Dayjs | null) => {
    const nextDueDate = toIsoStartOfDay(nextValue);

    if (nextDueDate === toIsoStartOfDay(dueDate)) {
      setAnchorEl(null);
      return;
    }

    try {
      await updateTask({
        taskId,
        payload: {
          due_date: nextDueDate,
        },
      }).unwrap();
      onUpdated?.();
      setAnchorEl(null);
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <span className={styles.updateTaskDueDate}>
      <button
        type="button"
        aria-label="Update due date"
        className={clsx(
          styles.updateTaskDueDate__trigger,
          !dueDate && styles.updateTaskDueDate__triggerUnset,
        )}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {label}
      </button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <div className={styles.updateTaskDueDate__pickerWrap}>
          <DatePicker
            mode="single"
            name={`task-due-date-${taskId}`}
            value={dueDate}
            label="Due date"
            size="small"
            onChange={(nextValue) => {
              void handleDateChange(nextValue);
            }}
          />
        </div>
      </Popover>
    </span>
  );
}
